import { useCallback, useEffect, useRef } from "react";

import { useSnackbar } from "notistack";

import { trpc } from "../../../../../common/helpers/trpc";
import { useBreakpointLogger } from "../../../../../common/hooks/useBreakpointLogger";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { PDFContentViewer } from "@/components/PDFPreview/PDFContentViewer";
import { PDFPreview } from "@/components/PDFPreview/PDFPreview";

export const MembershipCardLivePreview = () => {
  const breakpoint = useBreakpointLogger();
  const { liveFormData } = useLiveFormEditorContext<"ticketing">();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  const ticketingId = liveFormData?.ticketing.id;

  const {
    data: membershipCardHtml,
    isLoading,
    isError,
  } = trpc.getPreviewMembershipCardInHTML.useQuery(
    {
      ticketingId: liveFormData?.ticketing.id || "",
    },
    {
      enabled: Boolean(ticketingId && liveFormData?.ticketing),
    }
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const updateBannerInIframe = useCallback(() => {
    if (!liveFormData?.ticketing || !liveFormData?.ticketing.generateQrCode) {
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }

    const bannerContainer = iframeDoc.querySelector(".banner-container") as HTMLDivElement;
    if (!bannerContainer) {
      return;
    }

    bannerContainer.style.display = "block";
  }, [liveFormData?.ticketing, iframeRef]);

  useEffect(() => {
    if (membershipCardHtml) {
      updateBannerInIframe();
    }
  }, [membershipCardHtml, updateBannerInIframe]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("common", "unknownError", { code: 119 }), { vibe: "danger" });
    }
  }, [isError, enqueueSnackbar, t]);

  // If the ticketing object says no QR code, return null
  if (!liveFormData?.ticketing?.generateQrCode) {
    return null;
  }

  return (
    <PDFPreview iframeRef={iframeRef} dataTest="preview-membership-card-iframe" title="Membership Card Preview">
      <PDFContentViewer
        html={membershipCardHtml}
        breakpoint={breakpoint}
        isSkeleton={!ticketingId || isLoading}
        width={556}
        height={351}
        scaleSettings={{
          xl: 0.9,
          lg: 0.7,
          md: 0.6,
          xs: 0.6,
        }}
      />
    </PDFPreview>
  );
};
